import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { SettingsProvider } from "@/lib/settings";
import { CurrencyProvider } from "@/lib/currency";
import "@/App.css";

import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

import Home from "@/pages/Home";
import Flights from "@/pages/Flights";
import FlightSearchPanel from "@/pages/FlightSearchPanel";
import FlightTravelers from "@/pages/FlightTravelers";
import FlightAncillaries from "@/pages/FlightAncillaries";
import FlightReview from "@/pages/FlightReview";
import Hotels from "@/pages/Hotels";
import Cars from "@/pages/Cars";
import Packages from "@/pages/Packages";
import Sightseeing from "@/pages/Sightseeing";
import Contact from "@/pages/Contact";
import Checkout from "@/pages/Checkout";
import PaymentStatus from "@/pages/PaymentStatus";
import MyBookings from "@/pages/MyBookings";
import BookingDetails from "@/pages/BookingDetails";
import BookingAddons from "@/pages/BookingAddons";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminCancellations from "@/pages/admin/AdminCancellations";
import AdminCancelRequests from "@/pages/admin/AdminCancelRequests";
import AdminUsers, { AdminUsersAdmins, AdminUsersCustomers } from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminBranding from "@/pages/admin/AdminBranding";
import SitePage from "@/pages/SitePage";
import SiteServiceGuard from "@/components/site/SiteServiceGuard";
import AdminPricingRulesLayout from "@/pages/admin/AdminPricingRulesLayout";
import AdminMarkupRules from "@/pages/admin/AdminMarkupRules";
import AdminConvenienceFees from "@/pages/admin/AdminConvenienceFees";
import AdminServiceCatalog from "@/pages/admin/AdminServiceCatalog";
import AdminPages from "@/pages/admin/AdminPages";
import AdminCurrencies from "@/pages/admin/AdminCurrencies";
import AdminFx from "@/pages/admin/AdminFx";
import AdminAirlineCommissions from "@/pages/admin/AdminAirlineCommissions";
import AdminAirlineTourCodes from "@/pages/admin/AdminAirlineTourCodes";
import AdminAirlineAccountCodes from "@/pages/admin/AdminAirlineAccountCodes";
import AdminAccountCodeAudit from "@/pages/admin/AdminAccountCodeAudit";
import AdminSupplierLogs from "@/pages/admin/AdminSupplierLogs";
import AdminAirlineLogos from "@/pages/admin/AdminAirlineLogos";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminAPIMaster from "@/pages/admin/AdminAPIMaster";
import AdminAds from "@/pages/admin/AdminAds";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminPromos from "@/pages/admin/AdminPromos";
import AdminRefunds from "@/pages/admin/AdminRefunds";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminContactMessages from "@/pages/admin/AdminContactMessages";
import AdminReports from "@/pages/admin/AdminReports";
import AdminCustomReport from "@/pages/admin/AdminCustomReport";
import AdminBookingDetail from "@/pages/admin/AdminBookingDetail";

function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#f8faf9]">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <Toaster position="bottom-center" richColors />
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/flights/search" element={<PublicLayout><SiteServiceGuard flagKey="product_flight" label="Flights"><FlightSearchPanel /></SiteServiceGuard></PublicLayout>} />
            <Route path="/flights" element={<PublicLayout><SiteServiceGuard flagKey="product_flight" label="Flights"><Flights /></SiteServiceGuard></PublicLayout>} />
            <Route path="/flights/travelers" element={<PublicLayout><SiteServiceGuard flagKey="product_flight" label="Flights"><FlightTravelers /></SiteServiceGuard></PublicLayout>} />
            <Route path="/flights/ancillaries" element={<PublicLayout><SiteServiceGuard flagKey="product_flight" label="Flights"><FlightAncillaries /></SiteServiceGuard></PublicLayout>} />
            <Route path="/flights/review" element={<PublicLayout><SiteServiceGuard flagKey="product_flight" label="Flights"><FlightReview /></SiteServiceGuard></PublicLayout>} />
            <Route path="/hotels" element={<PublicLayout><SiteServiceGuard flagKey="product_hotel" label="Hotels"><Hotels /></SiteServiceGuard></PublicLayout>} />
            <Route path="/cars" element={<PublicLayout><SiteServiceGuard flagKey="product_car" label="Cars"><Cars /></SiteServiceGuard></PublicLayout>} />
            <Route path="/packages" element={<PublicLayout><SiteServiceGuard flagKey="product_package" label="Packages"><Packages /></SiteServiceGuard></PublicLayout>} />
            <Route path="/sightseeing" element={<PublicLayout><SiteServiceGuard flagKey="product_sightseeing" label="Sightseeing"><Sightseeing /></SiteServiceGuard></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><SitePage slug="about" /></PublicLayout>} />
            <Route path="/careers" element={<PublicLayout><SitePage slug="careers" /></PublicLayout>} />
            <Route path="/press" element={<PublicLayout><SitePage slug="press" /></PublicLayout>} />
            <Route path="/partners" element={<PublicLayout><SitePage slug="partners" /></PublicLayout>} />
            <Route path="/checkout/:bookingId" element={<PublicLayout><Checkout /></PublicLayout>} />
            <Route path="/payment-status" element={<PublicLayout><PaymentStatus /></PublicLayout>} />
            <Route path="/my-bookings" element={<PublicLayout><MyBookings /></PublicLayout>} />
            <Route path="/bookings/:bookingId" element={<PublicLayout><BookingDetails /></PublicLayout>} />
            <Route path="/bookings/:bookingId/addons" element={<PublicLayout><BookingAddons /></PublicLayout>} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="bookings/:bookingId" element={<AdminBookingDetail />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="cancel-requests" element={<AdminCancelRequests />} />
              <Route path="cancellations" element={<AdminCancellations />} />
              <Route path="refunds" element={<AdminRefunds />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="reports/custom" element={<AdminCustomReport />} />
              <Route path="users/admins" element={<AdminUsersAdmins />} />
              <Route path="users/customers" element={<AdminUsersCustomers />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="pricing-rules" element={<AdminPricingRulesLayout />}>
                <Route index element={<AdminMarkupRules />} />
                <Route path="markup" element={<AdminMarkupRules />} />
                <Route path="convenience-fees" element={<AdminConvenienceFees />} />
              </Route>
              <Route path="service-catalog" element={<AdminServiceCatalog />} />
              <Route path="currencies" element={<AdminCurrencies />} />
              <Route path="fx" element={<AdminFx />} />
              <Route path="airline-commissions" element={<AdminAirlineCommissions />} />
              <Route path="airline-tour-codes" element={<AdminAirlineTourCodes />} />
              <Route path="airline-account-codes" element={<AdminAirlineAccountCodes />} />
              <Route path="account-code-audit" element={<AdminAccountCodeAudit />} />
              <Route path="supplier-logs" element={<AdminSupplierLogs />} />
              <Route path="airline-logos" element={<AdminAirlineLogos />} />
              <Route path="content/pages" element={<AdminPages />} />
              <Route path="suppliers" element={<AdminSuppliers />} />
              <Route path="api-master" element={<AdminAPIMaster />} />
              <Route path="ads" element={<AdminAds />} />
              <Route path="packages" element={<AdminPackages />} />
              <Route path="promos" element={<AdminPromos />} />
              <Route path="contact-messages" element={<AdminContactMessages />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="branding" element={<AdminBranding />} />
            </Route>
          </Routes>
          </BrowserRouter>
        </CurrencyProvider>
      </AuthProvider>
    </SettingsProvider>
  );
}
